import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FEST_DATE } from '../data/camp-data';

function getTimeLeft() {
  const now = new Date();
  const diff = FEST_DATE.getTime() - now.getTime();

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isLive: true };
  }

  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    isLive: false,
  };
}

function TimeUnit({ value, label, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6 }}
      className="flex flex-col items-center"
    >
      <div className="relative">
        <div className="glass glow-border-cyan rounded-xl w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center">
          <span className="font-heading text-2xl sm:text-3xl font-black text-white tabular-nums">
            {String(value).padStart(2, '0')}
          </span>
        </div>
      </div>
      <span className="mt-2 text-[10px] tracking-[0.2em] uppercase font-mono text-[var(--mc-text-muted)]">
        {label}
      </span>
    </motion.div>
  );
}

export default function CountdownTimer() {
  const [time, setTime] = useState(getTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (time.isLive) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
        data-testid="countdown-live"
      >
        <p className="font-heading text-xl sm:text-2xl font-bold neon-text-cyan uppercase tracking-wider animate-pulse">
          Oktober Fest is LIVE
        </p>
      </motion.div>
    );
  }

  return (
    <div className="text-center" data-testid="countdown-timer">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="text-xs tracking-[0.25em] uppercase font-mono text-[var(--mc-cyan)] mb-4"
      >
        Countdown to Oktober Fest
      </motion.p>
      <div className="flex items-center justify-center gap-3 sm:gap-5">
        <TimeUnit value={time.days} label="Dagar" delay={2.0} />
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.3, 1] }}
          transition={{ delay: 2.2, duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
          className="text-[var(--mc-pink)] font-heading text-xl sm:text-2xl mt-[-18px]"
        >
          :
        </motion.span>
        <TimeUnit value={time.hours} label="Timmar" delay={2.1} />
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.3, 1] }}
          transition={{ delay: 2.3, duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
          className="text-[var(--mc-pink)] font-heading text-xl sm:text-2xl mt-[-18px]"
        >
          :
        </motion.span>
        <TimeUnit value={time.minutes} label="Min" delay={2.2} />
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.3, 1] }}
          transition={{ delay: 2.4, duration: 1, repeat: Infinity, repeatDelay: 0.5 }}
          className="text-[var(--mc-pink)] font-heading text-xl sm:text-2xl mt-[-18px]"
        >
          :
        </motion.span>
        <TimeUnit value={time.seconds} label="Sek" delay={2.3} />
      </div>
    </div>
  );
}
